import { useState } from "react";
import { FileDown, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";

// Color by verdict (structural — not shown as label)
const VERDICT_COLORS = {
  FAKE:      { bg: "#1a0505", border: "#ef4444", text: "#ef4444" },
  REAL:      { bg: "#021a09", border: "#22c55e", text: "#22c55e" },
  UNCERTAIN: { bg: "#1a1105", border: "#f59e0b", text: "#f59e0b" },
  SUSPICIOUS:{ bg: "#1a1105", border: "#f59e0b", text: "#f59e0b" },
};

// Risk-based label per MVP spec
function getRiskLabel(verdict, confidence, isID) {
  const v = (verdict || "UNCERTAIN").toUpperCase();
  const c = parseFloat(confidence) || 0;
  if (v === "FAKE")       return c >= 80 ? (isID ? "RISIKO TINGGI" : "HIGH RISK") : (isID ? "RISIKO SEDANG" : "MEDIUM RISK");
  if (v === "REAL")       return isID ? "RISIKO RENDAH" : "LOW RISK";
  if (v === "SUSPICIOUS") return isID ? "RISIKO SEDANG" : "MEDIUM RISK";
  return isID ? "TAK PASTI" : "UNCERTAIN";
}

// Wrap and truncate text to maxLines, appending "..." if truncated
function wrapText(ctx, text, maxW, maxLines) {
  const words = text.split(" ");
  let line = "";
  const lines = [];

  for (let i = 0; i < words.length; i++) {
    const test = line ? `${line} ${words[i]}` : words[i];
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = words[i];
      if (lines.length >= maxLines) {
        // Truncate last line with ellipsis
        let trunc = lines[maxLines - 1];
        while (ctx.measureText(trunc + "...").width > maxW && trunc.length > 4) {
          trunc = trunc.slice(0, -2);
        }
        lines[maxLines - 1] = trunc + "...";
        return lines;
      }
    } else {
      line = test;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  return lines;
}

export function ReportBadge({ result }) {
  const { t, i18n } = useTranslation();
  const [generating, setGenerating] = useState(false);

  if (!result?.share_id && !result?.id) return null;

  const shareUrl = `${window.location.origin}/share/${result.share_id || result.id}`;
  const verdict  = (result.verdict || "UNCERTAIN").toUpperCase();
  const meta     = VERDICT_COLORS[verdict] || VERDICT_COLORS.UNCERTAIN;
  const isID     = (i18n.language || "id").split("-")[0] === "id";

  const downloadBadge = async () => {
    setGenerating(true);
    try {
      const W = 620, H = 230;
      const canvas = document.createElement("canvas");
      canvas.width  = W * 2;
      canvas.height = H * 2;
      const ctx = canvas.getContext("2d");
      ctx.scale(2, 2);

      // Background
      ctx.fillStyle = "#0d0d0d";
      ctx.fillRect(0, 0, W, H);

      // Border
      ctx.strokeStyle = meta.border;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(1, 1, W - 2, H - 2);

      // Left accent bar
      ctx.fillStyle = meta.border;
      ctx.fillRect(0, 0, 4, H);

      // Header
      ctx.fillStyle = "#555";
      ctx.font = "bold 9px 'Courier New', monospace";
      ctx.fillText("VERIFIED BY VERIKSA", 20, 21);

      // Content title
      const titleText = result.content_title || result.video_name || "";
      if (titleText) {
        ctx.fillStyle = "#aaaaaa";
        ctx.font = "bold 10px 'Arial', sans-serif";
        const maxTitleW = 360;
        let dt = titleText;
        while (ctx.measureText(dt).width > maxTitleW && dt.length > 15) {
          dt = dt.slice(0, -4) + "...";
        }
        ctx.fillText(dt, 20, 35);
      }

      // ── Risk label (replaces raw "DEEPFAKE") ──────────────────────────
      const riskLabel = getRiskLabel(verdict, result.confidence, isID);
      ctx.fillStyle = meta.text;
      ctx.font = "bold 44px 'Arial', sans-serif";
      ctx.fillText(riskLabel, 18, 90);

      // ── Estimasi Risiko label (replaces "CONFIDENCE") ─────────────────
      const riskEstLabel = isID ? "ESTIMASI RISIKO AI" : "AI RISK ESTIMATE";
      ctx.fillStyle = "#888";
      ctx.font = "11px 'Courier New', monospace";
      ctx.fillText(riskEstLabel, 20, 114);
      ctx.fillStyle = meta.text;
      ctx.font = "bold 28px 'Arial', sans-serif";
      ctx.fillText(`${Math.round(result.confidence || 0)}%`, 20, 147);

      // Excerpt — max 120 chars, max 2 lines with proper ellipsis
      const descText = (result.excerpt || result.summary || "").slice(0, 120);
      if (descText) {
        ctx.fillStyle = "#666";
        ctx.font = "11px 'Arial', sans-serif";
        const lines = wrapText(ctx, descText, 340, 2);
        lines.forEach((l, i) => ctx.fillText(l, 20, 170 + i * 15));
      }

      // Date
      const dateStr = result.created_at
        ? new Date(result.created_at).toLocaleDateString(isID ? "id-ID" : "en-US",
            { day: "numeric", month: "long", year: "numeric" })
        : new Date().toLocaleDateString(isID ? "id-ID" : "en-US");
      ctx.fillStyle = "#888";
      ctx.font = "10px 'Courier New', monospace";
      ctx.fillText(dateStr, 20, 202);

      // QR Code
      const qrDataUrl = await QRCode.toDataURL(shareUrl, {
        width: 120, margin: 1,
        color: { dark: "#ffffff", light: "#00000000" },
      });
      const qrImg = new Image();
      await new Promise((res, rej) => { qrImg.onload = res; qrImg.onerror = rej; qrImg.src = qrDataUrl; });
      const qrSize = 110, qrX = W - qrSize - 16, qrY = (H - qrSize) / 2 - 5;
      ctx.fillStyle = "#1a1a1a";
      ctx.fillRect(qrX - 6, qrY - 6, qrSize + 12, qrSize + 12);
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
      ctx.fillStyle = "#555";
      ctx.font = "8px 'Courier New', monospace";
      ctx.textAlign = "center";
      ctx.fillText("SCAN TO VERIFY", qrX + qrSize / 2, qrY + qrSize + 16);
      ctx.textAlign = "left";

      // Footer
      ctx.strokeStyle = "#1f1f1f";
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(20, H - 18); ctx.lineTo(W - 20, H - 18); ctx.stroke();
      ctx.fillStyle = "#777";
      ctx.font = "9px 'Courier New', monospace";
      ctx.fillText("veriksa.id  •  AI Media Risk Assessment", 20, H - 6);

      const link = document.createElement("a");
      link.download = `veriksa-badge-${verdict.toLowerCase()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();

      toast.success(isID ? "Badge berhasil diunduh!" : "Badge downloaded!");
    } catch (err) {
      console.error("Badge generation error:", err);
      toast.error(isID ? "Gagal membuat badge. Coba lagi." : "Badge generation failed. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Button
      variant="outline" size="sm"
      onClick={downloadBadge} disabled={generating}
      className="rounded-sm gap-1.5 text-xs"
      data-testid="download-badge-btn"
    >
      <Award className={`w-3.5 h-3.5 ${generating ? "animate-pulse" : ""}`} />
      {generating ? (isID ? "Membuat..." : "Generating...") : t("results.downloadBadge", "Unduh Badge")}
    </Button>
  );
}
